import { useEffect } from 'react';
import { Menu, MenuItem, Sidebar, sidebarClasses } from 'react-pro-sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Badge, Box, Divider } from '@mui/material';

import BrandLogo from '../../../branding/BrandLogo';
import { setSidebarCollapsed } from '../../../store/actions/layout';
import { setUserAfterLogin } from '../../../store/actions/login';
import { UserAccessLevel, UserRole } from '../../../types/user';
import { useUnlockRequestsCount } from '../../../hooks/useUnlockRequestsCount';
import { APP_ROUTES } from '../../../utils/constants/appRoutes';
import {
  DeviceGroupAdminPlusAndViewer,
  SuperAdminAndViewer,
  SuperAdminOnly,
} from '../../../utils/constants/users';
import { isSuperAdmin as isSuperAdminUser, parseAccessLevel } from '../../../utils/helpers/organizationAccess';
import { getRoleString } from '../../../utils/helpers/getRoleString';
import HelpButton from '../../common/HelpButton';
import LegalLinks from '../../legal/LegalLinks';
import BurgerMenuIcon from '../../Icons/BurgerMenuIcon';
import DevicesMenuIcon from '../../Icons/DevicesMenuIcon';
import DynamicDataLogIcon from '../../Icons/DynamicDataLogIcon';
import FaultEvenLogMenuIcon from '../../Icons/FaultEvenLogMenuIcon';
import GroupsMenuIcon from '../../Icons/GroupsMenuIcon';
import MultiSpeakProvidersMenuIcon from '../../Icons/MultispeakProvidersMenuIcon';
import NotificationMenuIcon from '../../Icons/NotificationMenuIcon';
import OrganizationsMenuIcon from '../../Icons/OrganizationsMenuIcon';
import PowerLinesMenuIcon from '../../Icons/PowerLinesMenuIcon';
import UsersMenuIcon from '../../Icons/UsersMenuIcon';

interface MenuItemStyleProps {
  active?: boolean;
}

interface CustomSidebarProps {
  onCollapse: (collapsed: boolean) => void;
}

const CustomSidebar = ({ onCollapse }: CustomSidebarProps) => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user.user);
  const isCollapsed = useSelector((state: any) => state.layout.isSidebarCollapsed);
  const accessLevel = parseAccessLevel(user);
  const userRole = getRoleString(accessLevel) as UserRole;
  const isSuperAdmin = isSuperAdminUser(user);
  const { count: unlockRequestsCount } = useUnlockRequestsCount(!!isSuperAdmin);

  useEffect(() => {
    dispatch(setUserAfterLogin());
  }, [dispatch]);

  const handleCollapse = (collapsed: boolean) => {
    dispatch(setSidebarCollapsed(collapsed));
    onCollapse(collapsed);
  };

  return (
    <Sidebar
      collapsed={isCollapsed}
      transitionDuration={500}
      rootStyles={{
        backgroundColor: '#FDFDFD',
        padding: '12px',
        borderRight: '1px solid #EFEFEF',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        [`.${sidebarClasses.container}`]: {
          backgroundColor: 'transparent',
          transition: 'all 1s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <div className="sidebar-header">
        <div
          id="toggle-button"
          className="burger-menu"
          onClick={() => handleCollapse(!isCollapsed)}
        >
          <BurgerMenuIcon />
        </div>
        <BrandLogo
          style={{
            display: 'block',
            height: '24px',
            width: 'auto',
            opacity: isCollapsed ? 0 : 1,
            visibility: isCollapsed ? 'hidden' : 'visible',
          }}
        />
      </div>
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Menu
          menuItemStyles={{
            button: ({ active }: MenuItemStyleProps) => {
              return {
                color: active ? '#05629F' : '#616161',
                backgroundColor: active ? '#F0F8FF' : undefined,
                borderRadius: '12px',
                fontSize: '16px',
                lineHeight: '24px',
                gap: '6px',
                padding: '12px',
                '&:hover': {
                  backgroundColor: '#F0F8FF',
                  color: '#05629F',
                },
              };
            },
          }}
          id="sidebar-menu"
          closeOnClick={false}
        >
          <MenuItem
            component={<Link to={APP_ROUTES.HOME} />}
            active={window.location.pathname === APP_ROUTES.HOME}
            icon={<DevicesMenuIcon />}
          >
            Devices
          </MenuItem>
          <MenuItem
            component={<Link to={APP_ROUTES.CRITICAL_DATA} />}
            active={window.location.pathname === APP_ROUTES.CRITICAL_DATA}
            icon={<FaultEvenLogMenuIcon />}
          >
            Fault Event Log
          </MenuItem>
          <MenuItem
            component={<Link to={APP_ROUTES.DYNAMIC_DATA_LOG} />}
            active={window.location.pathname === APP_ROUTES.DYNAMIC_DATA_LOG}
            icon={<DynamicDataLogIcon />}
          >
            Dynamic Data Log
          </MenuItem>
          {DeviceGroupAdminPlusAndViewer.includes(userRole) && (
            <MenuItem
              active={window.location.pathname === APP_ROUTES.USERS}
              component={<Link to={APP_ROUTES.USERS} />}
              icon={
                isSuperAdmin ? (
                  <Badge badgeContent={unlockRequestsCount} color="error" showZero={false}>
                    <UsersMenuIcon />
                  </Badge>
                ) : (
                  <UsersMenuIcon />
                )
              }
            >
              Users
            </MenuItem>
          )}
          {DeviceGroupAdminPlusAndViewer.includes(userRole) && (
            <MenuItem
              active={window.location.pathname === APP_ROUTES.GROUPS}
              component={<Link to={APP_ROUTES.GROUPS} />}
              icon={<GroupsMenuIcon />}
            >
              Groups
            </MenuItem>
          )}
          {SuperAdminAndViewer.includes(userRole) && (
            <MenuItem
              active={window.location.pathname === APP_ROUTES.POWER_LINES}
              component={<Link to={APP_ROUTES.POWER_LINES} />}
              icon={<PowerLinesMenuIcon />}
            >
              Power Lines
            </MenuItem>
          )}
          {SuperAdminOnly.includes(userRole) && (
            <MenuItem
              active={window.location.pathname === APP_ROUTES.ORGANIZATIONS}
              component={<Link to={APP_ROUTES.ORGANIZATIONS} />}
              icon={<OrganizationsMenuIcon />}
            >
              Organizations
            </MenuItem>
          )}
          {accessLevel === UserAccessLevel.ORGANIZATION_ADMIN && (
            <MenuItem
              active={window.location.pathname === APP_ROUTES.ORGANIZATION_SETTINGS}
              component={<Link to={APP_ROUTES.ORGANIZATION_SETTINGS} />}
              icon={<OrganizationsMenuIcon />}
            >
              Org Settings
            </MenuItem>
          )}
          {SuperAdminOnly.includes(userRole) && (
            <MenuItem
              active={window.location.pathname === APP_ROUTES.MS_PROVIDERS}
              component={<Link to={APP_ROUTES.MS_PROVIDERS} />}
              icon={<MultiSpeakProvidersMenuIcon />}
            >
              Multispeak Providers
            </MenuItem>
          )}
          <MenuItem
            active={window.location.pathname === APP_ROUTES.NOTIFICATION_CENTER}
            component={<Link to={APP_ROUTES.NOTIFICATION_CENTER} />}
            icon={<NotificationMenuIcon />}
          >
            Notification Center
          </MenuItem>
        </Menu>
      </Box>

      {/* Help Section at Bottom */}
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Divider sx={{ mb: 2, opacity: 0.3 }} />
        <HelpButton
          variant="sidebar"
          size="small"
          tooltip="Watch SGS Portal tutorial"
          videoUrl="https://www.youtube.com/watch?v=-nV91fjZB30"
          title="SGS Portal Tutorial"
          description="Watch our comprehensive tutorial to learn how to navigate and use all features of the SGS Portal, from logging in to managing devices, notifications, and more."
          isCollapsed={isCollapsed}
          enableSupportSection
        />
        {!isCollapsed && <LegalLinks variant="sidebar" />}
      </Box>
    </Sidebar>
  );
};

export default CustomSidebar;
